import React, {FC, useContext} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownItemRightIcon} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemRightIcon';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {isParticipantRemovable} from '../../../../../../../srcCommon/inboxes/viewer/conversation/participants/inviteParticipantsHelpers';
import {ParticipantTrackerItemStatus} from '../../../../../../../srcCommon/inboxes/viewer/conversation/participants/participantTrackerItemStatus';
import {PushContext} from '../../../../../../../srcCommon/push/pushContext';
import {buildTeammateDisplayName} from '../../../../../../helpers/modelHelpers';
import {TeammateAvatar} from '../../../../../common/avatars/teammateAvatar';
import {InboxesTopbarTooltip} from '../../../../topbar/inboxesTopbarTooltip';
import {VisualConversationTrackerModel} from './inviteHelpers';

/*
 * Props.
 */

interface InviteDropdownTrackerItemProps {
  index?: number;
  tracker: VisualConversationTrackerModel;
  onRemoveClick?: Handler;
  isUserVisitor: boolean;
  onSubscribeClick?: Handler;
  onUnsubscribeClick?: Handler;
  isFaded?: boolean;
  isUserAssignee?: boolean;
}

/*
 * Style.
 */

const StyledAvatarDiv = styled.div`
  grid-area: left-icon;
  margin-right: 8px;
`;

const StyledStatusDiv = styled.div`
  grid-area: secondary-content;
  justify-self: end;
  align-self: center;
  margin-left: 6px;
  max-width: 100%;

  color: ${(p) => p.theme.greys.shade60};
  line-height: 20px;
  white-space: nowrap;
`;

const StyledBaseButtonDiv = styled.div`
  display: flex;

  grid-area: secondary-content / none;
  justify-self: end;

  height: 20px;
  overflow: visible;
`;

const StyledButtonDiv = styled(StyledBaseButtonDiv)`
  margin-top: -3px;
  padding-right: 1px;
`;

const StyledDisabledButtonDiv = styled(StyledBaseButtonDiv)`
  padding: 2px 14px 0 0;

  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledButtonText = styled.div`
  display: flex;
`;

const StyledIcon = styled(Icon)`
  margin: 2px 5px 0 0;
`;

/*
 * Component.
 */

export const InviteDropdownTrackerItem: FC<InviteDropdownTrackerItemProps> = (props) => {
  const {isConnected} = useContext(PushContext);

  return (
    <DropdownItem
      index={props.index}
      renderRightContent={({isHighlighted}) => renderRightContent(props, isConnected, isHighlighted)}
    >
      {renderAvatar(props)}
      <DropdownItemContent>{buildTeammateDisplayName(props.tracker.teammate)}</DropdownItemContent>
    </DropdownItem>
  );
};

function renderAvatar(props: InviteDropdownTrackerItemProps) {
  const {teammate, hasSeenAll} = props.tracker;

  return (
    <StyledAvatarDiv>
      <TeammateAvatar
        teammate={teammate}
        size={VisualSizesEnum.MEDIUM}
        isFaded={props.isFaded ?? !hasSeenAll}
      />
    </StyledAvatarDiv>
  );
}

function renderRightContent(
  props: InviteDropdownTrackerItemProps,
  isConnected: boolean,
  isHighlighted?: boolean,
) {
  const {tracker, isUserVisitor, onSubscribeClick, onUnsubscribeClick, onRemoveClick, isUserAssignee} = props;
  const isRemoveDisabled = !isConnected;
  const canRemoveParticipant =
    onRemoveClick && isParticipantRemovable(tracker) && !isRemoveDisabled && !isUserVisitor;

  if (isHighlighted && canRemoveParticipant) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <DropdownItemRightIcon
            label={<Trans id="invite-removeparticipant">Remove participant</Trans>}
            rightIconName="removeSmall"
            rightIconColor={theme.greys.shade60}
            rightIconHoverColor={theme.greys.shade70}
            onRightIconClick={props.onRemoveClick}
            renderTooltip={() => <Trans id="invite-removeaccess">Remove access</Trans>}
          />
        )}
      </UserThemeConsumer>
    );
  }

  const shouldShowSubscriptionButtons = onSubscribeClick && onUnsubscribeClick;

  return (
    <>
      {!shouldShowSubscriptionButtons && (
        <StyledStatusDiv>
          <ParticipantTrackerItemStatus
            teammate={tracker.teammate}
            isUser={tracker.isUser}
            isUserOrDelegatedTeammate={tracker.isUserOrDelegatedTeammate}
            isOwner={tracker.isOwner}
            isInboxMember={tracker.isInboxMember}
            hasSeenAll={tracker.hasSeenAll}
            hasSnoozed={tracker.hasSnoozed}
            hasArchived={tracker.hasArchived}
            hasMuted={tracker.hasMuted}
            seenAt={tracker.seenAt}
            isHighlighted={isHighlighted}
          />
        </StyledStatusDiv>
      )}
      {shouldShowSubscriptionButtons &&
        maybeRenderSubscriptionButton({tracker, onSubscribeClick, onUnsubscribeClick, isUserAssignee})}
    </>
  );
}

interface MaybeRenderSubscriptionButtonParams {
  tracker: VisualConversationTrackerModel;
  onSubscribeClick: Handler;
  onUnsubscribeClick: Handler;
  isUserAssignee?: boolean;
}

function maybeRenderSubscriptionButton(params: MaybeRenderSubscriptionButtonParams) {
  const {tracker, onSubscribeClick, onUnsubscribeClick, isUserAssignee} = params;
  if (!tracker.isUserOrDelegatedTeammate) {
    return null;
  }

  const subscribeText = <Trans id="invitedropdown-trackeritem-subscribe">Subscribe</Trans>;
  const unsubscribeText = <Trans id="invitedropdown-trackeritem-unsubscribe">Unsubscribe</Trans>;

  const buttonText = (
    <StyledButtonText>
      <StyledIcon name={tracker.hasMuted ? 'subscribeTiny' : 'unsubscribeTiny'} />
      {tracker.hasMuted ? subscribeText : unsubscribeText}
    </StyledButtonText>
  );

  const renderSubscribeTooltip = () => (
    <InboxesTopbarTooltip
      keyboardAction={KeyboardActionsEnum.SUBSCRIBE}
      firstLine={<>{subscribeText}</>}
      secondLine={
        <Trans id="invitedropdown-trackeritem-subscribetooltip">
          Will get updates for all new activities
        </Trans>
      }
    />
  );

  const renderUnsubscribeTooltip = () => (
    <InboxesTopbarTooltip
      keyboardAction={KeyboardActionsEnum.SUBSCRIBE}
      firstLine={<>{unsubscribeText}</>}
      secondLine={
        <Trans id="invitedropdown-trackeritem-unsubscribetooltip">
          <ul>
            <li>Will not get updates unless @mentioned</li>
            <li>Will archive the conversation in your inbox</li>
          </ul>
        </Trans>
      }
    />
  );

  const renderAssigneeUnsubscribeTooltip = () => (
    <Trans id="invitedropdown-trackeritem-assignee-cannotunsubscribetooltip">
      You cannot unsubscribe if you are assigned.
    </Trans>
  );

  if (isUserAssignee) {
    return (
      <StyledDisabledButtonDiv>
        <TooltipCoordinator render={renderAssigneeUnsubscribeTooltip}>{buttonText}</TooltipCoordinator>
      </StyledDisabledButtonDiv>
    );
  }

  return (
    <StyledButtonDiv>
      <Button
        onClick={tracker.hasMuted ? onSubscribeClick : onUnsubscribeClick}
        variant={ButtonVariantsEnum.LINK}
        size={VisualSizesEnum.SMALL}
        renderTooltip={tracker.hasMuted ? renderSubscribeTooltip : renderUnsubscribeTooltip}
        shouldUseDirectHover
      >
        {buttonText}
      </Button>
    </StyledButtonDiv>
  );
}
